import { useCallback, useEffect, useState, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useTranslation } from "react-i18next";
import { RTL_LANGS, pickContentLang } from "@majourneys/shared";
import { cn } from "../../lib/utils";

import { Icon } from "@majourneys/icons";
interface CarouselProps {
  children: ReactNode;
  gap?: number;
  arrows?: boolean;
  dots?: boolean;
  className?: string;
}

export function Carousel({
  children,
  gap = 30,
  arrows = true,
  dots = false,
  className,
}: CarouselProps) {
  const { t, i18n } = useTranslation();
  // Embla lays slides out left to right unless told otherwise; on Arabic pages
  // the track must start at the right edge and drag the other way.
  const isRtl = RTL_LANGS.includes(pickContentLang(i18n.language));
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: isRtl ? "rtl" : "ltr",
    slidesToScroll: 1,
    align: "start",
    containScroll: "trimSnaps",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setSlideCount(emblaApi.slideNodes().length);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className={cn("relative", className)}>
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex" style={{ gap: `${gap}px` }}>
          {children}
        </div>
      </div>

      {arrows && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="absolute top-1/2 -start-4 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border-main bg-white text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-30"
            aria-label={t("common.previous")}
          >
            <Icon name="ri-arrow-left-s-line" className="text-[20px] rtl:-scale-x-100" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="absolute top-1/2 -end-4 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border-main bg-white text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-30"
            aria-label={t("common.next")}
          >
            <Icon name="ri-arrow-right-s-line" className="text-[20px] rtl:-scale-x-100" />
          </button>
        </>
      )}

      {dots && slideCount > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {Array.from({ length: slideCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => emblaApi?.scrollTo(i)}
              // 24px hit area meets WCAG 2.5.8 target-size; the visible
              // pill stays small via the inner span so the design is
              // unchanged.
              className="flex h-6 w-6 items-center justify-center"
              aria-label={`Go to slide ${i + 1}`}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "block h-2 rounded-full transition-all duration-300",
                  i === selectedIndex ? "w-8 bg-primary" : "w-2 bg-primary/30",
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CarouselSlide({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex-[0_0_100%] min-w-0", className)}>
      {children}
    </div>
  );
}
